import React, { useState, useEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../../styles/glassmorphism';
import { KpiCardProps } from '../types/dashboard.types';

const KpiCard: React.FC<KpiCardProps> = ({ 
  title, 
  value, 
  unit = '', 
  icon, 
  trend, 
  status = 'good', 
  type 
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cores por status
  const statusColors = {
    good: '#27ae60',
    warning: '#f39c12',
    critical: '#e74c3c'
  };

  // Cores por tipo
  const typeColors: Record<string, string> = {
    status: '#3498db',
    conversion: '#9b59b6',
    responseTime: '#e67e22',
    quality: '#1abc9c'
  };

  const primaryColor = typeColors[type] || '#667eea';

  return (
    <div
      style={{
        ...GlassmorphismTheme.components.glassCard,
        background: `linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(${
          type === 'status' ? '52, 152, 219' : type === 'conversion' ? '155, 89, 182' : type === 'responseTime' ? '230, 126, 34' : '26, 188, 156'
        }, 0.05))`,
        border: `1px solid ${primaryColor}33`,
        padding: isMobile ? '8px' : '12px',
        textAlign: 'center',
        transition: GlassmorphismTheme.transitions.normal,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
        (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px rgba(${
          type === 'status' ? '52, 152, 219' : type === 'conversion' ? '155, 89, 182' : type === 'responseTime' ? '230, 126, 34' : '26, 188, 156'
        }, 0.2)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLElement).style.boxShadow = GlassmorphismTheme.shadows.glassSoft;
      }}
    >
      {/* ICON */}
      <div style={{ marginBottom: isMobile ? '4px' : '6px' }}>
        <Icon
          name={icon as any}
          size="large"
          style={{
            color: primaryColor,
            filter: 'drop-shadow(0 4px 8px rgba(176, 196, 222, 0.2))'
          }}
        />
      </div>

      {/* TITLE */}
      <h4 style={{
        margin: `0 0 ${isMobile ? '4px' : '6px'} 0`,
        color: '#7f8c8d',
        fontSize: isMobile ? '0.75rem' : '0.8rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {title}
      </h4>

      {/* VALUE */}
      <div style={{
        fontSize: isMobile ? '1.4rem' : '1.6rem',
        fontWeight: 700,
        color: primaryColor,
        marginBottom: isMobile ? '4px' : '6px',
        lineHeight: 1
      }}>
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
        {unit && <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#95a5a6', marginLeft: '4px' }}>{unit}</span>}
      </div>

      {/* TREND */}
      {trend !== undefined && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          marginTop: isMobile ? '4px' : '6px',
          padding: isMobile ? '2px 6px' : `${GlassmorphismTheme.spacing.sm} ${GlassmorphismTheme.spacing.md}`,
          background: trend > 0 ? 'rgba(39, 174, 96, 0.1)' : 'rgba(231, 76, 60, 0.1)',
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          fontSize: isMobile ? '0.7rem' : '0.8rem',
          fontWeight: 600
        }}>
          <Icon
            name={trend > 0 ? 'arrow up' : 'arrow down'}
            style={{ color: trend > 0 ? '#27ae60' : '#e74c3c' }}
          />
          <span style={{ color: trend > 0 ? '#27ae60' : '#e74c3c' }}>
            {Math.abs(trend)}%
          </span>
        </div>
      )}

      {/* STATUS INDICATOR */}
      {status && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          marginTop: GlassmorphismTheme.spacing.md,
          fontSize: '0.85rem'
        }}>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: statusColors[status],
              boxShadow: `0 0 10px ${statusColors[status]}60`
            }}
          />
          <span style={{ color: statusColors[status], fontWeight: 600 }}>
            {status === 'good' ? 'Bom' : status === 'warning' ? 'Aviso' : 'Crítico'}
          </span>
        </div>
      )}
    </div>
  );
};

export default KpiCard;
